import { AllEffect, CallEffect, ForkEffect, GetContextEffect, PutEffect, SelectEffect } from 'redux-saga/effects';
import { all, call, getContext, put, select, takeEvery, takeLatest } from 'typed-redux-saga';
import {
  buildWidgetMachinesReportFilter,
  MACHINE_DASHBOARD_WIDGET_LIST_PAGE_SIZE,
  mergeWidgetMachineLists,
  resolveWidgetListQueryPlan,
} from '../../utils/machineDashboardWidgetListUtils';
import { MachineDashboardWidgetId } from '../../types/machineDashboardWidgetListState.types';
import * as MachineDashboardActionsTypes from '../../types/machineDashboardActions.types';
import { MachineDashboardActions } from './machineDashboardSlice';
import { MachineDashboardSelectors } from './machineDashboardSelectors';
import { IDependencies } from 'app/cross-cutting-concerns/dependency-injection/interfaces/IDependencies';
import {
  ManualMachineDashboardResponse,
  MachineDashboardReportList,
  MachineList,
} from 'app/modules/machine-inventory/interfaces/Machine.types';
import { SiteList } from 'app/modules/site-management/interfaces/Site.types';
import { getNextPaginationToken } from 'app/utils/infinite-scroll/infiniteScroll';
import { SortOrders } from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { DateTime } from 'lib/utils/date-handling/DateTime';
import { OpenSearch } from 'config/constants';

const fetchWidgetMachineList = async (
  machineService: IDependencies['machineService'],
  widgetId: MachineDashboardWidgetId,
  period: MachineDashboardActionsTypes.MachineDashboardWidgetListRequestAction['payload']['period'],
  siteIds: string[] | undefined,
  machineIds: string[] | undefined,
  limit: number,
  paginationToken: string
): Promise<MachineDashboardReportList> =>
  machineService.listMachinesReportForDashboard({
    period,
    search: '',
    filter: buildWidgetMachinesReportFilter(widgetId, siteIds, machineIds),
    sortOptions: {
      field: 'metadata.name',
      order: SortOrders.Asc,
    },
    paginationOptions: {
      limit,
      paginationToken,
    },
    timezone: DateTime.getBrowserTimeZone(),
  });

export function* manualMachineDashboardSaga(
  action: MachineDashboardActionsTypes.MachineDashboardManualMachineDashboardRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<ManualMachineDashboardResponse>
  | PutEffect<MachineDashboardActionsTypes.MachineDashboardManualMachineDashboardSuccessAction>
  | PutEffect<MachineDashboardActionsTypes.MachineDashboardManualMachineDashboardErrorAction>,
  void,
  IDependencies
> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(machineService.manualMachineDashboard, action.payload);

    yield* put(MachineDashboardActions.machineDashboardManualMachineDashboardSuccess(response.manualMachineDashboard));
  } catch (error) {
    console.error(error);

    yield* put(MachineDashboardActions.machineDashboardManualMachineDashboardError({ error }));
  }
}

export function* machineDashboardWidgetListRequestSaga(
  action: MachineDashboardActionsTypes.MachineDashboardWidgetListRequestAction
): Generator<
  GetContextEffect | SelectEffect | CallEffect<MachineDashboardReportList> | AllEffect<any> | PutEffect,
  void,
  IDependencies
> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');
  const { widgetId, period, siteIds, machineIds } = action.payload;

  try {
    const allSites = yield* select(MachineDashboardSelectors.selectSitesListData);
    const allNoSiteMachines = yield* select(MachineDashboardSelectors.selectNoSiteMachinesData);
    const plan = resolveWidgetListQueryPlan(
      siteIds,
      machineIds,
      (allSites ?? []).map(site => site.id),
      (allNoSiteMachines ?? []).map(machine => machine.id)
    );

    if (plan.type === 'none') {
      yield* put(
        MachineDashboardActions.machineDashboardWidgetListSuccess({
          widgetId,
          machines: [],
          thumbnailUrlsByMachineId: {},
          nextPaginationToken: '',
          hasMore: false,
          isLoadMore: false,
          period,
          siteIds,
          machineIds,
        })
      );

      return;
    }

    if (plan.type === 'merge') {
      // Both dimensions are genuinely restrictive at once — the backend can't OR them in a single
      // request, so fetch each half separately (bounded, unpaginated) and merge client-side. "Load
      // more" isn't supported for this combination; hasMore is always false here.
      const [siteScoped, machineScoped] = yield* all([
        call(
          fetchWidgetMachineList,
          machineService,
          widgetId,
          period,
          plan.siteIds,
          undefined,
          OpenSearch.MAX_RESULT_WINDOW,
          ''
        ),
        call(
          fetchWidgetMachineList,
          machineService,
          widgetId,
          period,
          undefined,
          plan.machineIds,
          OpenSearch.MAX_RESULT_WINDOW,
          ''
        ),
      ]);

      const mergedMachines = mergeWidgetMachineLists([
        siteScoped.machinesReport.data,
        machineScoped.machinesReport.data,
      ]);

      yield* put(
        MachineDashboardActions.machineDashboardWidgetListSuccess({
          widgetId,
          machines: mergedMachines,
          thumbnailUrlsByMachineId: {
            ...siteScoped.thumbnailUrlsByMachineId,
            ...machineScoped.thumbnailUrlsByMachineId,
          },
          nextPaginationToken: '',
          hasMore: false,
          isLoadMore: false,
          period,
          siteIds,
          machineIds,
        })
      );

      return;
    }

    const response = yield* call(
      fetchWidgetMachineList,
      machineService,
      widgetId,
      period,
      plan.siteIds,
      plan.machineIds,
      MACHINE_DASHBOARD_WIDGET_LIST_PAGE_SIZE,
      ''
    );
    const machines = response.machinesReport.data;
    const paginationToken = response.machinesReport.metadata?.paginationToken ?? '';
    const totalCount = response.machinesReport.metadata?.totalCount ?? 0;

    yield* put(
      MachineDashboardActions.machineDashboardWidgetListSuccess({
        widgetId,
        machines,
        thumbnailUrlsByMachineId: response.thumbnailUrlsByMachineId,
        nextPaginationToken: getNextPaginationToken(paginationToken) ?? '',
        hasMore: Boolean(paginationToken) && machines.length < totalCount,
        isLoadMore: false,
        period,
        siteIds: plan.siteIds,
        machineIds: plan.machineIds,
      })
    );
  } catch (error) {
    console.error(error);

    yield* put(
      MachineDashboardActions.machineDashboardWidgetListError({
        error,
        widgetId,
        isLoadMore: false,
      })
    );
  }
}

export function* machineDashboardWidgetListLoadMoreSaga(
  action: MachineDashboardActionsTypes.MachineDashboardWidgetListLoadMoreRequestAction
): Generator<
  GetContextEffect | SelectEffect | CallEffect<MachineDashboardReportList> | PutEffect,
  void,
  IDependencies
> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');
  const { widgetId } = action.payload;
  const widgetListState = yield* select(MachineDashboardSelectors.selectWidgetListState(widgetId));

  if (!widgetListState.requestContext || !widgetListState.hasMore) {
    if (widgetListState.isLoadingMore) {
      yield* put(
        MachineDashboardActions.machineDashboardWidgetListError({
          error: new Error('Widget list load more skipped'),
          widgetId,
          isLoadMore: true,
        })
      );
    }

    return;
  }

  const { period, siteIds, machineIds } = widgetListState.requestContext;

  try {
    const response = yield* call(
      fetchWidgetMachineList,
      machineService,
      widgetId,
      period,
      siteIds,
      machineIds,
      MACHINE_DASHBOARD_WIDGET_LIST_PAGE_SIZE,
      widgetListState.nextPaginationToken
    );
    const machines = response.machinesReport.data;
    const paginationToken = response.machinesReport.metadata?.paginationToken ?? '';
    const totalCount = response.machinesReport.metadata?.totalCount ?? 0;

    yield* put(
      MachineDashboardActions.machineDashboardWidgetListSuccess({
        widgetId,
        machines,
        thumbnailUrlsByMachineId: response.thumbnailUrlsByMachineId,
        nextPaginationToken: getNextPaginationToken(paginationToken) ?? '',
        hasMore: Boolean(paginationToken) && widgetListState.machines.length + machines.length < totalCount,
        isLoadMore: true,
        period,
        siteIds,
        machineIds,
      })
    );
  } catch (error) {
    console.error(error);

    yield* put(
      MachineDashboardActions.machineDashboardWidgetListError({
        error,
        widgetId,
        isLoadMore: true,
      })
    );
  }
}

export function* getSitesListSaga(
  action: MachineDashboardActionsTypes.MachineDashboardSitesListRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<SiteList>
  | PutEffect<MachineDashboardActionsTypes.MachineDashboardSitesListSuccessAction>
  | PutEffect<MachineDashboardActionsTypes.MachineDashboardSitesListErrorAction>,
  void,
  IDependencies
> {
  const { siteService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(siteService.listForSelect, action.payload);
    yield* put(MachineDashboardActions.machineDashboardSitesListSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(MachineDashboardActions.machineDashboardSitesListError({ error }));
  }
}

export function* getNoSiteMachinesSaga(): Generator<
  | GetContextEffect
  | CallEffect<MachineList>
  | PutEffect<MachineDashboardActionsTypes.MachineDashboardNoSiteMachinesSuccessAction>
  | PutEffect<MachineDashboardActionsTypes.MachineDashboardNoSiteMachinesErrorAction>,
  void,
  IDependencies
> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(machineService.listRobots, {
      filter: {
        isStandalone: true,
      },
      paginationOptions: {
        limit: OpenSearch.MAX_RESULT_WINDOW,
      },
    });

    const machines = response.machines.data.map(machine => ({ id: machine.id, name: machine.name }));

    yield* put(MachineDashboardActions.machineDashboardNoSiteMachinesSuccess({ machines }));
  } catch (error) {
    console.error(error);
    yield* put(MachineDashboardActions.machineDashboardNoSiteMachinesError({ error }));
  }
}

export function* machineDashboardSaga(): Generator<ForkEffect<never | unknown>, void> {
  yield* takeLatest(MachineDashboardActions.machineDashboardManualMachineDashboardRequest, manualMachineDashboardSaga);
  yield* takeEvery(MachineDashboardActions.machineDashboardWidgetListRequest, machineDashboardWidgetListRequestSaga);
  yield* takeLatest(
    MachineDashboardActions.machineDashboardWidgetListLoadMoreRequest,
    machineDashboardWidgetListLoadMoreSaga
  );
  yield* takeLatest(MachineDashboardActions.machineDashboardSitesListRequest, getSitesListSaga);
  yield* takeLatest(MachineDashboardActions.machineDashboardNoSiteMachinesRequest, getNoSiteMachinesSaga);
}
